import { yupResolver } from '@hookform/resolvers/yup';
import {
  PAGE_DEFAULT,
  SORT_ATTRIBUTE_ORDERS_DEFAULT,
  SORT_ORDER_DEFAULT
} from 'constants/defaults';
import {
  InputMaybe,
  PurchaseOrderPage,
  PurchaseOrderSortAttributeEnum,
  QueryPurchaseOrdersArgs,
  SortOrderEnum
} from 'gql/graphql';
import { usePathname, useSearchParams } from 'next/navigation';
import { useOrders } from 'providers/OrdersProviders/useOrders';
import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Control, useForm, useWatch } from 'react-hook-form';
import { KeysEnum } from 'types/KeysEnum';
import { TScheme } from 'types/TScheme';
import { extractParam } from 'utils/paramsUtil';
import * as yup from 'yup';

export type FiltersOrdersForm = Omit<
  QueryPurchaseOrdersArgs,
  'page' | 'perPage' | 'skipPagination'
>;

interface IUseSearchParamsOrdersOut {
  orders?: PurchaseOrderPage;
  isLoading: boolean;
  isPending: boolean;
  isRetrieving: boolean;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  totalPages: number;
  handleClear: () => void;
  control: Control<FiltersOrdersForm>;
  filtersAdded: boolean;
  sortAttribute?: InputMaybe<PurchaseOrderSortAttributeEnum>;
  sortOrder?: InputMaybe<SortOrderEnum>;
}

export default function useSearchParamsOrders(): IUseSearchParamsOrdersOut {
  const schema = yup.object().shape({} as TScheme<FiltersOrdersForm>);

  const pathname = usePathname();
  const searchParams = useSearchParams();

  const [page, setPage] = useState(PAGE_DEFAULT);

  const {
    control,
    setValue,
    formState: { isDirty }
  } = useForm<FiltersOrdersForm>({
    resolver: yupResolver(schema)
  });

  const pageInitial = useMemo(
    () => extractParam(searchParams, KeysEnum.Page, PAGE_DEFAULT) as number,
    [searchParams]
  );

  const sortAttributeInitial = extractParam(
    searchParams,
    KeysEnum.SortAttribute,
    SORT_ATTRIBUTE_ORDERS_DEFAULT
  );

  const sortOrderInitial = extractParam(searchParams, KeysEnum.SortOrder, SORT_ORDER_DEFAULT);

  const sortAttribute = useWatch<FiltersOrdersForm>({
    control,
    name: KeysEnum.SortAttribute,
    defaultValue: SORT_ATTRIBUTE_ORDERS_DEFAULT
  }) as InputMaybe<PurchaseOrderSortAttributeEnum>;

  const sortOrder = useWatch<FiltersOrdersForm>({
    control,
    name: KeysEnum.SortOrder,
    defaultValue: SORT_ORDER_DEFAULT
  }) as InputMaybe<SortOrderEnum>;

  const filtersAdded = useMemo(
    () =>
      [
        page !== PAGE_DEFAULT,
        sortOrderInitial !== SORT_ORDER_DEFAULT,
        sortAttributeInitial !== SORT_ATTRIBUTE_ORDERS_DEFAULT
      ].some(Boolean),
    [page, sortAttributeInitial, sortOrderInitial]
  );

  const input: QueryPurchaseOrdersArgs = {
    sortAttribute,
    sortOrder,
    page
  };

  const { data: orders, isLoading, isPending } = useOrders(input);

  const totalPages = orders?.pagesCount ?? 0;

  const isRetrieving = isLoading && page === 1;

  const resetFilters = useCallback(() => {
    setPage(pageInitial);
    setValue(KeysEnum.SortAttribute, sortAttributeInitial);
    setValue(KeysEnum.SortOrder, sortOrderInitial);
  }, [pageInitial, sortAttributeInitial, sortOrderInitial, setValue]);

  const handleClear = useCallback(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      url.searchParams.delete(KeysEnum.Page);
      url.searchParams.delete(KeysEnum.SortAttribute);
      url.searchParams.delete(KeysEnum.SortOrder);

      resetFilters();

      window.history.replaceState({}, '', url.toString());
    }
  }, [resetFilters]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (sortOrder && sortAttribute)
        url.searchParams.set(KeysEnum.SortOrder, JSON.stringify(sortOrder));
      else url.searchParams.delete(KeysEnum.SortOrder);

      if (sortAttribute && sortOrder)
        url.searchParams.set(KeysEnum.SortAttribute, JSON.stringify(sortAttribute));
      else url.searchParams.delete(KeysEnum.SortAttribute);

      url.searchParams.delete(KeysEnum.Page);
      setPage([sortOrder && sortAttribute].some(Boolean) && isDirty ? PAGE_DEFAULT : pageInitial);

      window.history.replaceState({}, '', url.toString());
    }
  }, [pageInitial, sortOrder, sortAttribute, isDirty]);
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);

      if (page > PAGE_DEFAULT) url.searchParams.set(KeysEnum.Page, JSON.stringify(page));
      else url.searchParams.delete(KeysEnum.Page);

      window.history.replaceState({}, '', url.toString());
    }
  }, [page, pathname]);

  return {
    orders,
    isLoading,
    isPending,
    isRetrieving,
    page,
    setPage,
    totalPages,
    handleClear,
    control,
    filtersAdded,
    sortAttribute,
    sortOrder
  };
}
